import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReactToPrint } from 'react-to-print';
import { Button } from './button';
import { toast } from 'sonner';
import { ScrollIcon, PrintIcon, SaveIcon, CloseIcon, RefreshIcon, SparkleIcon } from './icons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const SummaryExporter = ({ isOpen, onClose, projectId, projectName, sections }) => {
  const [summary, setSummary] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [sectionsCompleted, setSectionsCompleted] = useState(0);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${projectName} - Community Charter`,
  });

  const generateSummary = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });

      if (!response.ok) throw new Error('Failed to generate summary');

      const data = await response.json();
      setSummary(data.summary);
      setSectionsCompleted(data.sections_completed);
      toast.success('Summary generated!');
    } catch (error) {
      console.error('Summary error:', error);
      toast.error('Failed to generate summary');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadMarkdown = () => {
    let markdown = `# ${projectName}\n\n## Community Charter\n\n`;
    markdown += `*Generated on ${new Date().toLocaleDateString()}*\n\n---\n\n`;
    
    sections.forEach(section => {
      markdown += `## ${section.title}\n\n`;
      markdown += section.content || '*Section not yet completed*';
      markdown += '\n\n---\n\n';
    });

    if (summary) {
      markdown += `## Summary\n\n${summary}\n`;
    }

    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName.replace(/\s+/g, '-').toLowerCase()}-charter.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Markdown downloaded!');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-[var(--card)] rounded-[var(--radius-lg)] shadow-lg max-w-3xl w-full my-8 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-[var(--muted)]/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--accent)]/10 flex items-center justify-center">
                <ScrollIcon size={20} className="text-[var(--accent)]" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-[var(--fg)]">
                  Charter Summary & Export
                </h3>
                <p className="text-xs text-[var(--fg)]/60">
                  Generate and export your community charter
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-[var(--muted)] flex items-center justify-center transition-colors"
            >
              <CloseIcon size={16} className="text-[var(--fg)]/60" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Generate button */}
            {!summary && (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--sky)] flex items-center justify-center">
                  <SparkleIcon size={40} className="text-[var(--primary)]" />
                </div>
                <h4 className="font-heading text-xl text-[var(--fg)] mb-2">
                  Generate Your Charter Summary
                </h4>
                <p className="text-sm text-[var(--fg)]/60 max-w-md mx-auto mb-6">
                  M.ai.ry will create a beautiful summary of your community charter,
                  highlighting key agreements and offering wisdom for the journey ahead.
                </p>
                <Button
                  onClick={generateSummary}
                  disabled={isGenerating}
                  size="lg"
                  data-testid="summary-generate-button"
                >
                  {isGenerating ? (
                    <>
                      <RefreshIcon size={16} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <SparkleIcon size={16} />
                      Generate Summary
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Summary preview */}
            {summary && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-heading text-lg text-[var(--fg)]">{projectName}</h4>
                    <p className="text-xs text-[var(--fg)]/60">
                      {sectionsCompleted} of {sections.length} sections completed
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={downloadMarkdown}
                      data-testid="summary-download-button"
                    >
                      <SaveIcon size={16} />
                      Markdown
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handlePrint}
                      data-testid="summary-print-button"
                    >
                      <PrintIcon size={16} />
                      Print PDF
                    </Button>
                  </div>
                </div>

                {/* Printable content */}
                <div
                  ref={printRef}
                  className="bg-white border border-[var(--muted)]/50 rounded-[var(--radius-md)] p-6 max-h-[60vh] overflow-y-auto"
                >
                  <div className="text-center mb-8 print:mb-12">
                    <h1 className="font-heading text-3xl text-[var(--fg)] mb-2">{projectName}</h1>
                    <p className="text-sm text-[var(--fg)]/60">Community Charter</p>
                    <p className="text-xs text-[var(--fg)]/40 mt-2">
                      Generated {new Date().toLocaleDateString()}
                    </p>
                  </div>

                  <div className="prose prose-sm max-w-none">
                    <h2 className="font-heading text-xl text-[var(--fg)] mb-4">Summary</h2>
                    <div className="text-[var(--fg)]/80 whitespace-pre-wrap leading-relaxed">
                      {summary}
                    </div>
                  </div>

                  <hr className="my-8 border-[var(--muted)]" />

                  {sections.map((section) => (
                    <div key={section.key} className="mb-8 print-page">
                      <h3 className="font-heading text-lg text-[var(--fg)] mb-3">
                        {section.title}
                      </h3>
                      <p className="text-sm text-[var(--fg)]/70 whitespace-pre-wrap">
                        {section.content || 'Section not yet completed.'}
                      </p>
                    </div>
                  ))}

                  <div className="mt-12 pt-8 border-t border-[var(--muted)] text-center">
                    <p className="text-xs text-[var(--fg)]/40">
                      Built with love using Bliss Abode • Trust as Currency • Belonging Sessions Family
                    </p>
                    <p className="text-[10px] text-[var(--fg)]/30 mt-1">
                      blissabode.org
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};